import { useState } from 'react';
import QuestionAnswerIcon from '../../../assets/icons/faq.svg?url';
import CopyIcon from '../../../assets/icons/copy.svg?url';
import ExitToAppIcon from '../../../assets/icons/signout.svg?url';
import UserTransferIcon from '../../../assets/icons/user-transfer.svg?url';
import AvatarMenu from './avatar-menu';
import Dialog from '../dialog';
import Input from '../input';
import { Button } from '../button';
import l from '../../../lang';
import { getLanguage, openNewTabAndFocus, redirecTo } from '../../../utils';
import { Urls } from '../../../config/urls';
import { useDashboard } from '../../../store/dashboard';
import { useAuth } from '../../../store/auth';
import { postTask } from '../../../services';

const AvatarOptions = () => {
  const { userMe, merchantMe } = useDashboard();
  const { sendLogout, token } = useAuth();
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [merchantUserEmail, setMerchantUserEmail] = useState('');
  const [impersonateError, setImpersonateError] = useState('');
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const canImpersonate = !!merchantMe?.features?.includes('MERCHANT_IMPERSONATION');

  const getDocumentationUrl = () => {
    const lang = getLanguage();
    switch (lang) {
      case 'es':
        return Urls.faq.es;
      case 'en':
        return Urls.faq.en;
      case 'pt':
        return Urls.faq.pt;
      default:
        return Urls.faq.en;
    }
  };

  const getFeedbackUrl = () => {
    const lang = getLanguage();
    switch (lang) {
      case 'es':
        return Urls.feedback.es;
      case 'en':
        return Urls.feedback.en;
      case 'pt':
        return Urls.feedback.pt;
      default:
        return Urls.feedback.en;
    }
  };

  const openFAQs = () => {
    const urlFaq = getDocumentationUrl();
    openNewTabAndFocus(urlFaq);
  };

  const openFeedback = () => {
    const urlFaq = getFeedbackUrl();
    openNewTabAndFocus(urlFaq);
  };

  const openImpersonationDialog = () => {
    setMerchantUserEmail('');
    setImpersonateError('');
    setImpersonateOpen(true);
  };

  const handleImpersonate = async () => {
    const email = merchantUserEmail.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!isValidEmail) {
      setImpersonateError(l('impersonation.merchantUserEmailRequired'));
      return;
    }

    if (!token || !userMe?.email) {
      setImpersonateError(l('impersonation.error'));
      return;
    }

    setImpersonateLoading(true);
    const { response, error } = await postTask({
      url: Urls.api.impersonateMerchant,
      token,
      body: {
        merchantUserEmail: email,
        adminUserEmail: userMe.email,
      },
    });
    setImpersonateLoading(false);

    if (response?.redirectUrl) {
      setImpersonateOpen(false);
      redirecTo(response.redirectUrl);
      return;
    }

    setImpersonateError(error?.message || l('impersonation.error'));
  };

  const menuOptions = [
    ...(canImpersonate
      ? [
          {
            icon: <img src={UserTransferIcon} />,
            text: l('avatarOptions.impersonate'),
            onClick: () => openImpersonationDialog(),
          },
        ]
      : []),
    {
      icon: <img src={QuestionAnswerIcon} />,
      text: l('avatarOptions.FAQ'),
      onClick: () => openFAQs(),
    },
    {
      icon: <img src={CopyIcon} />,
      text: l('avatarOptions.feedback'),
      onClick: () => openFeedback(),
    },
    {
      icon: <img src={ExitToAppIcon} />,
      text: l('avatarOptions.logout'),
      onClick: () => sendLogout(),
    },
  ];

  return (
    <div>
      <AvatarMenu user={userMe?.name} items={menuOptions} />
      {impersonateOpen && (
        <Dialog
          dividers={false}
          type="sm"
          title={l('impersonation.title')}
          handleClose={() => setImpersonateOpen(false)}
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Input
                name="merchantUserEmail"
                label={l('impersonation.merchantUserEmail')}
                value={merchantUserEmail}
                onFocus={null}
                onChange={e => {
                  setMerchantUserEmail(e.target.value);
                  if (impersonateError) {
                    setImpersonateError('');
                  }
                }}
                error={impersonateError}
              />
              <Button onClick={handleImpersonate} disabled={impersonateLoading}>
                {l('impersonation.button')}
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default AvatarOptions;
